import { apiClient } from '@/lib/apiClient'
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  LoginInput,
  LoginResponse,
  RegisterAccountInput,
  RegisterGameInput,
  RegisterResponse,
  RegisterVerifyInput,
  ResetPasswordInput,
  UpdateProfileInput,
  User,
  VerifyCodeResponse,
} from '@/types/auth'
import type { GameAccount } from '@/types/game-account'

type ApiEnvelope<T> = {
  code: number
  data: T
  message: string
}

type VerificationStatusResponse = {
  verified: boolean
}

type ForgotPasswordResponse = {
  code: string
  expiresAt: string
}

export interface UserSecuritySettings {
  allowResumeTrust: boolean
  allowWebLoginTrust: boolean
  resumeTrustTtlMinutes: number
  webLoginTrustTtlMinutes: number
}

export interface RecommendedQQGroup {
  id: string
  groupId: string
  groupName: string
  groupJoinLink?: string
  memberCount: number
  maxMemberCount?: number
  sortOrder: number
}

export interface VerificationServiceStatus {
  available: boolean
  onlineBotCount: number
  availableGroupCount: number
}

export async function login(input: LoginInput): Promise<LoginResponse> {
  const response = await apiClient.post<ApiEnvelope<LoginResponse>>('/public/auth/login', {
    account: input.account,
    password: input.password,
  })
  return response.data
}

export async function registerAccount(input: RegisterAccountInput): Promise<RegisterResponse> {
  const response = await apiClient.post<ApiEnvelope<RegisterResponse>>('/public/auth/register', {
    username: input.username,
    qqNumber: input.qqNumber,
    password: input.password,
    confirmPassword: input.confirmPassword,
  })
  return response.data
}

export async function verifyRegisterCode(input: RegisterVerifyInput): Promise<VerifyCodeResponse> {
  const response = await apiClient.post<ApiEnvelope<VerifyCodeResponse>>('/public/auth/verify', {
    qqNumber: input.qqNumber,
    type: 'register',
    ...(input.verificationCode ? { verificationCode: input.verificationCode } : {}),
  })
  return response.data
}

export async function registerGameAccount(input: RegisterGameInput): Promise<GameAccount> {
  const response = await apiClient.post<ApiEnvelope<GameAccount>>('/user/game-accounts', {
    gameUsername: input.gameUsername,
    gamePassword: input.gamePassword,
  })
  return response.data
}

export async function checkVerificationStatus(
  qqNumber: string,
  type: 'register' | 'reset_password'
): Promise<VerificationStatusResponse> {
  const queryParams = new URLSearchParams({
    qqNumber,
    type,
  })

  const response = await apiClient.get<ApiEnvelope<VerificationStatusResponse>>(
    `/public/auth/verification-status?${queryParams.toString()}`
  )
  return response.data
}

export async function forgotPassword(
  input: ForgotPasswordInput
): Promise<ForgotPasswordResponse> {
  const response = await apiClient.post<ApiEnvelope<ForgotPasswordResponse>>(
    '/public/auth/forgot-password',
    {
      qqNumber: input.qqNumber,
    }
  )
  return response.data
}

export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await apiClient.post<ApiEnvelope<null>>('/public/auth/reset-password', {
    qqNumber: input.qqNumber,
    newPassword: input.newPassword,
    confirmPassword: input.confirmPassword,
  })
}

export async function changePassword(input: ChangePasswordInput): Promise<void> {
  await apiClient.put<ApiEnvelope<null>>('/user/password', {
    oldPassword: input.currentPassword,
    newPassword: input.newPassword,
  })
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<ApiEnvelope<User>>('/public/auth/me')
  return response.data
}

export async function updateProfile(input: UpdateProfileInput): Promise<User> {
  const response = await apiClient.put<ApiEnvelope<User>>('/user/profile', input)
  return response.data
}

export async function getUserSecuritySettings(): Promise<UserSecuritySettings> {
  const response = await apiClient.get<ApiEnvelope<UserSecuritySettings>>('/user/security')
  return response.data
}

export async function updateUserSecuritySettings(
  input: Partial<UserSecuritySettings>
): Promise<UserSecuritySettings> {
  const response = await apiClient.put<ApiEnvelope<UserSecuritySettings>>('/user/security', input)
  return response.data
}

export async function logout(): Promise<void> {
  await apiClient.post<ApiEnvelope<null>>('/public/auth/logout')
}

export async function getQQAuthorizeUrl(): Promise<string> {
  const response = await apiClient.get<ApiEnvelope<{ authorizeUrl: string }>>('/public/auth/qq/authorize')
  return response.data.authorizeUrl
}

export async function completeQQLogin(code: string, state: string): Promise<LoginResponse> {
  const response = await apiClient.post<ApiEnvelope<LoginResponse>>(
    '/public/auth/qq/callback',
    { code, state },
    { skipAuthRefresh: true, skipAuthHeader: true }
  )
  return response.data
}

export async function getRecommendedQQGroups(limit: number = 3): Promise<RecommendedQQGroup[]> {
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
  })

  const response = await apiClient.get<ApiEnvelope<RecommendedQQGroup[]>>(
    `/public/qq-groups/recommended?${queryParams.toString()}`,
    { skipAuthRefresh: true }
  )
  return response.data
}

export async function getVerificationServiceStatus(): Promise<VerificationServiceStatus> {
  const response = await apiClient.get<ApiEnvelope<VerificationServiceStatus>>(
    '/public/qq-groups/verification-service-status',
    { skipAuthRefresh: true }
  )
  return response.data
}
